import {get} from 'lodash';

import type {RunWithSupportedKeys} from '../../../util/runs';
import {
  type GroupSelectionState,
  runIdsToNames,
  type SelectionState,
} from '../../../util/selectionmanager';
import * as SelectionManager from '../../../util/selectionmanager';
import type {ActionType, ViewReducerState} from '../reducerSupport';
import type {RunSetPart} from '../runSet/types';
import {setTempSelections} from './actionsInternal';
import type {Ref as TempSelectionRef} from './types';

/**
 * Gets the IDs of all pinned runs and the baseline run from a run set.
 * @param runSet
 * @returns An array of run IDs that are either pinned or the baseline.
 */
export function getPinnedAndBaselineRunIds(runSet: RunSetPart): string[] {
  let pinnedRunIds = runSet.pinnedRunIds ?? [];
  if (runSet.baselineRunId) {
    pinnedRunIds = pinnedRunIds.concat(runSet.baselineRunId);
  }
  return pinnedRunIds;
}

/**
 * Retrieves the checkbox selection state for a given reference.
 *
 * @param viewState - The current view reducer state containing all parts.
 * @param ref - A reference to the checkbox selection part to retrieve.
 * @returns The SelectionState if found, or null if the reference doesn't exist.
 */
export function getTempSelections(
  viewState: ViewReducerState,
  ref: TempSelectionRef
): SelectionState | null {
  return get(viewState, ['parts', 'temp-selections', ref.id], null);
}

/**
 * Updates the checkbox selection state immutably using an updater function.
 *
 * Returns the original state if the selection doesn't exist, if the updater
 * returns the same reference, or if the updater throws an error.
 *
 * @param state - The current view reducer state.
 * @param ref - A reference to the checkbox selection part to update.
 * @param updater - A function that receives the current selection and returns the updated selection.
 * @returns A new state with the updated checkbox selection, or the original state if no update occurred.
 */
export function updateTempSelections(
  state: ViewReducerState,
  ref: TempSelectionRef,
  updater: (current: SelectionState) => SelectionState
): ViewReducerState {
  const currentSelections = getTempSelections(state, ref);
  if (!currentSelections) {
    return state;
  }
  try {
    const updatedSelections = updater(currentSelections);
    if (updatedSelections === currentSelections) {
      return state;
    }
    return {
      ...state,
      parts: {
        ...state.parts,
        'temp-selections': {
          ...state.parts['temp-selections'],
          [ref.id]: updatedSelections,
        },
      },
    };
  } catch (e) {
    console.error('Error updating tempSelections:', e);
    return state;
  }
}

/**
 * Updates the checkbox selection state and returns an inverse action for undo support.
 *
 * This function wraps updateTempSelections to provide undo/redo functionality by
 * capturing the previous state and creating an action that can restore it.
 *
 * @param state - The current view reducer state.
 * @param ref - A reference to the checkbox selection part to update.
 * @param updater - A function that receives the current selection and returns the updated selection.
 * @returns A tuple of [newState, inverseAction] where inverseAction can be used to undo the change.
 */
export function updateTempSelectionsWithUndo(
  state: ViewReducerState,
  ref: TempSelectionRef,
  updater: (current: SelectionState) => SelectionState
): [ViewReducerState, ActionType] {
  const currentSelections = getTempSelections(state, ref);
  if (!currentSelections) {
    return [state, {type: 'noop'} as any];
  }

  const newState = updateTempSelections(state, ref, updater);
  const inverseAction = setTempSelections(ref, currentSelections);
  return [newState, inverseAction];
}

/**
 * Selects all currently visible runs, including pinned and baseline runs.
 *
 * This copies the current group selection state (which represents what's visible
 * in the table) and also marks all pinned runs and the baseline run as selected.
 *
 * @param tempSelection - The current checkbox selection state.
 * @param runSet - The run set containing pinned and baseline run information.
 * @param groupSelection - The current group selection state representing visible runs.
 * @returns A new SelectionState with all visible runs selected, or the original if inputs are null.
 */
export function selectAllVisible(
  tempSelection: SelectionState,
  runSet: RunSetPart | null,
  groupSelection: GroupSelectionState | null
): SelectionState {
  if (!groupSelection || !runSet) {
    return tempSelection;
  }

  const newTempSelection = {...groupSelection.selections};
  newTempSelection.selectedPinnedRowNames = runIdsToNames(
    getPinnedAndBaselineRunIds(runSet)
  );

  return newTempSelection;
}

/**
 * Deselects all runs, clearing the checkbox selection.
 *
 * @param tempSelection - The current checkbox selection state.
 * @param groupSelection - The current group selection state for context.
 * @returns A new SelectionState with no runs selected, or the original if groupSelection is null.
 */
export function selectNone(
  tempSelection: SelectionState,
  groupSelection: GroupSelectionState | null
): SelectionState {
  if (!groupSelection) {
    return tempSelection;
  }
  return SelectionManager.selectNone({
    selections: tempSelection,
    grouping: groupSelection.grouping,
    expandedRowAddresses: groupSelection.expandedRowAddresses,
  }).selections;
}

/**
 * Toggles the checkbox selection state of a single run.
 *
 * If the run is currently selected, it will be deselected, and vice versa.
 * The depth parameter indicates the grouping level of the run in the table.
 *
 * @param tempSelection - The current checkbox selection state.
 * @param groupSelection - The current group selection state for context.
 * @param run - The run to toggle selection for.
 * @param depth - The depth level of the run in the grouping hierarchy.
 * @returns A new SelectionState with the run's selection toggled, or the original if groupSelection is null.
 */
export function selectToggle(
  tempSelection: SelectionState,
  groupSelection: GroupSelectionState | null,
  run: RunWithSupportedKeys,
  depth: number
): SelectionState {
  if (!groupSelection) {
    return tempSelection;
  }

  const state = {
    selections: tempSelection,
    grouping: groupSelection.grouping,
    expandedRowAddresses: groupSelection.expandedRowAddresses,
  };

  return SelectionManager.toggleSelection(state, run, depth).selections;
}

/**
 * Selects a specific set of runs, deselecting all others.
 *
 * This clears the current selection and then selects only the provided runs.
 *
 * @param tempSelection - The current checkbox selection state.
 * @param groupSelection - The current group selection state for context.
 * @param runs - The array of runs to select.
 * @returns A new SelectionState with only the specified runs selected, or the original if groupSelection is null.
 */
export function selectSome(
  tempSelection: SelectionState,
  groupSelection: GroupSelectionState | null,
  runs: RunWithSupportedKeys[]
): SelectionState {
  if (!groupSelection) {
    return tempSelection;
  }
  return SelectionManager.selectSome(
    {
      selections: tempSelection,
      grouping: groupSelection.grouping,
      expandedRowAddresses: groupSelection.expandedRowAddresses,
    },
    runs
  ).selections;
}

/**
 * Selects all runs in the run set, including pinned and baseline runs.
 *
 * Unlike selectAllVisible which only selects currently visible runs, this
 * selects all runs regardless of pagination or filtering, plus any pinned
 * and baseline runs.
 *
 * @param tempSelection - The current checkbox selection state.
 * @param runSet - The run set containing pinned and baseline run information.
 * @param groupSelection - The current group selection state for context.
 * @returns A new SelectionState with all runs selected, or the original if inputs are null.
 */
export function selectAll(
  tempSelection: SelectionState,
  runSet: RunSetPart | null,
  groupSelection: GroupSelectionState | null
): SelectionState {
  if (!groupSelection || !runSet) {
    return tempSelection;
  }

  return SelectionManager.selectAll({
    selections: tempSelection,
    grouping: groupSelection.grouping,
    expandedRowAddresses: groupSelection.expandedRowAddresses,
    pinnedRowIds: getPinnedAndBaselineRunIds(runSet),
  }).selections;
}
